// SPDX-License-Identifier: AGPL-3.0-or-later

import {ValidationErrorCodes} from '@fluxer/constants/src/ValidationErrorCodes';
import {createStringType} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {URLType} from '@fluxer/schema/src/primitives/UrlValidators';
import {z} from 'zod';

export const NAMEPLATE_ID_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const MAX_NAMEPLATE_CATALOG_ENTRIES = 100;
export const NAMEPLATE_VIDEO_EXTENSIONS = ['.mp4', '.webm'] as const;
export const NAMEPLATE_IMAGE_EXTENSIONS = ['.gif', '.webp', '.png'] as const;

export const NameplateIdType = createStringType(1, 64).pipe(
	z.string().regex(NAMEPLATE_ID_REGEX, ValidationErrorCodes.INVALID_FORMAT),
);

export const NameplateIdParam = z.object({
	id: NameplateIdType.describe('Hosted nameplate catalog id'),
});

export type NameplateIdParam = z.infer<typeof NameplateIdParam>;

export type NameplateMediaKind = 'video' | 'image';

export function getNameplateMediaPath(imageUrl: string): string {
	try {
		return new URL(imageUrl).pathname.toLowerCase();
	} catch {
		return '';
	}
}

export function getNameplateMediaKind(imageUrl: string): NameplateMediaKind | null {
	const pathname = getNameplateMediaPath(imageUrl);
	if (NAMEPLATE_VIDEO_EXTENSIONS.some((extension) => pathname.endsWith(extension))) {
		return 'video';
	}
	if (NAMEPLATE_IMAGE_EXTENSIONS.some((extension) => pathname.endsWith(extension))) {
		return 'image';
	}
	return null;
}

function hasAllowedNameplateMediaExtension(imageUrl: string): boolean {
	return getNameplateMediaKind(imageUrl) !== null;
}

export const NameplateCatalogEntryResponse = z
	.object({
		id: NameplateIdType.describe('Stable slug for this nameplate'),
		name: createStringType(1, 64).describe('Display name shown in the nameplate picker'),
		description: createStringType(1, 256).optional().describe('Optional subtitle shown under the nameplate name'),
		image: URLType.describe('HTTPS media URL for the nameplate gif or video'),
		fx: z
			.string()
			.regex(/^[a-z0-9-]{1,32}$/)
			.optional()
			.describe('Built-in animated renderer key; clients that know it draw the effect instead of the image'),
		poster: URLType.optional().describe('Small static first frame shown until the nameplate is hovered'),
		accessible: z.boolean().optional().describe('Whether the current catalog still allows selecting this nameplate'),
		pro_included: z.boolean().optional().describe('Included with an active Pro membership; usable without buying it'),
		price: z
			.string()
			.regex(/^[0-9]{1,7}(?:\.[0-9]{1,2})?$/)
			.optional()
			.describe('Store price (TRY); items with a price can be bought in the store'),
		shopier_product_id: z
			.string()
			.regex(/^[0-9]{1,20}$/)
			.optional()
			.describe('Shopier product that sells this item in the store'),
	})
	.refine((entry) => hasAllowedNameplateMediaExtension(entry.image), ValidationErrorCodes.INVALID_URL_FORMAT);

export type NameplateCatalogEntryResponse = z.infer<typeof NameplateCatalogEntryResponse>;

export const NameplateCatalogResponse = z.object({
	nameplates: z
		.array(NameplateCatalogEntryResponse)
		.max(MAX_NAMEPLATE_CATALOG_ENTRIES)
		.describe('Hosted nameplate catalog entries'),
});

export type NameplateCatalogResponse = z.infer<typeof NameplateCatalogResponse>;

export function sanitizeNameplateCatalog(input: unknown): Array<NameplateCatalogEntryResponse> {
	if (input == null || typeof input !== 'object') {
		return [];
	}
	const rawEntries = (input as {nameplates?: unknown}).nameplates;
	if (!Array.isArray(rawEntries)) {
		return [];
	}
	const seenIds = new Set<string>();
	const nameplates: Array<NameplateCatalogEntryResponse> = [];
	for (const rawEntry of rawEntries) {
		if (nameplates.length >= MAX_NAMEPLATE_CATALOG_ENTRIES) {
			break;
		}
		const parsed = NameplateCatalogEntryResponse.safeParse(rawEntry);
		if (!parsed.success) {
			continue;
		}
		if (seenIds.has(parsed.data.id)) {
			continue;
		}
		seenIds.add(parsed.data.id);
		nameplates.push({
			id: parsed.data.id,
			name: parsed.data.name,
			description: parsed.data.description,
			image: parsed.data.image,
			...(parsed.data.fx ? {fx: parsed.data.fx} : {}),
			accessible: parsed.data.accessible !== false,
			...(parsed.data.pro_included ? {pro_included: true} : {}),
			...(parsed.data.shopier_product_id ? {shopier_product_id: parsed.data.shopier_product_id} : {}),
			...(parsed.data.price ? {price: parsed.data.price} : {}),
		});
	}
	return nameplates;
}
